import type { components } from './discovery.generated';

type Schemas = components['schemas'];

export type DiscoveryCampaignDetail = Schemas['ServiceCampaignDetail'];
export type DiscoveryPersona = Schemas['Persona'];
export type DiscoveryPersonaQuestion = Schemas['PersonaQuestion'];
export type DiscoveryPersonaCheck = Schemas['PersonaCheck'];
export type DiscoveryPersonaSearch = Schemas['PersonaSearch'];
export type DiscoveryPersonaQuery = Schemas['PersonaQuery'];
export type DiscoveryPersonaSearchPreviewed = Schemas['ServicePersonaSearchPreviewed'];
export type DiscoveryVettingPolicy = Schemas['VettingPolicy'];
export type DiscoveryVettingCheck = Schemas['VettingCheck'];
export type DiscoveryVettingCatalog = Schemas['VettingCatalog'];
export type DiscoveryCatalogCheck = Schemas['CatalogCheck'];
export type DiscoveryOwnQuestion = Schemas['OwnQuestion'];
export type DiscoveryGenerateBody = Schemas['GenerateBody'];
export type DiscoveryGeneratedPersonas = Schemas['GeneratedPersonas'];
export type DiscoveryPersonaSearchBody = Schemas['ServicePersonaSearch'];
export type DiscoveryPersonaSearchPreviewBody = Schemas['ServicePersonaSearchPreview'];
export type DiscoveryPersonaChat = Schemas['ServicePersonaChat'];
export type DiscoveryPersonaWrite = Schemas['ServicePersonaWrite'];
export type DiscoveryQuestionCheckBody = Schemas['ServicePersonaCheck'];
export type DiscoveryPersonaQuestionChecked = Schemas['PersonaQuestionChecked'];
export type DiscoveryPersonaChatTurn = Schemas['ServicePersonaChatTurn'];
export type DiscoveryChatQuestion = Schemas['ChatQuestion'];
export type DiscoveryTraceEntry = Schemas['TraceEntryOut'];
export type DiscoveryTraceItem = Schemas['TraceItemOut'];
export type DiscoveryTraceEvent = Schemas['TraceEvent'];
export type DiscoveryTurnTrace = Schemas['PersonaTurnTrace'];
export type DiscoveryChatFact = Schemas['ChatFact'];
export type DiscoveryRailCheck = Schemas['RailCheck'];
export type DiscoveryChatThread = Schemas['ChatThread'];
export type DiscoveryChatThreadSummary = Schemas['ChatThreadSummary'];
export type DiscoveryChatThreads = Schemas['ChatThreads'];
export type DiscoveryChatPatch = Schemas['ServiceChatPatch'];
export type DiscoveryChatStart = Schemas['ServiceChatStart'];
export type DiscoveryServiceCampaignPatch = Schemas['ServiceCampaignPatch'];
export type DiscoveryCampaignRun = Schemas['CampaignRun'];

export type DiscoverySearch = Schemas['ServiceSearch'];
export type DiscoverySearchMode = DiscoverySearch['mode'];
export type DiscoverySearchFilters = Schemas['ServiceSearchFilters'];
export type DiscoverySearchTurn = Schemas['TurnIn'];
export type DiscoveryEstimateBody = Schemas['ServiceEstimate'];
export type DiscoveryCostQuote = Schemas['CostQuote'];
export type DiscoverySearchStarted = Schemas['SearchStarted'];
export type DiscoveryClarifyingQuestion = Schemas['ClarifyingQuestion'];
export type DiscoveryJobView = Schemas['ServiceJobView'];
export type DiscoveryRejected = Schemas['ServiceRejected'];
export type DiscoveryJobStep = Schemas['ServiceJobStep'];
export type DiscoveryJobProgress = Schemas['ServiceJobProgress'];
/**
 * Hand-written: the spec types a job's `status` as a plain string. Mirrors creator-discovery's `TERMINAL_JOB`
 * (`web/constants.py`) plus the live `queued` and `running`; a status added there but missing here is neither
 * live nor terminal, so a run polling on it stops.
 */
export const DISCOVERY_JOB_STATUSES = [
  'queued',
  'running',
  'done',
  'partial',
  'error',
  'cancelled',
  'stalled',
] as const;
export type DiscoveryJobStatus = (typeof DISCOVERY_JOB_STATUSES)[number];
export const isDiscoveryJobStatus = (status: string): status is DiscoveryJobStatus =>
  DISCOVERY_JOB_STATUSES.some((known) => known === status);
export type DiscoveryLiveFeed = Schemas['ServiceLiveFeed'];
export type DiscoveryLiveCreator = Schemas['ServiceLiveCreator'];
export type DiscoveryCancelBody = Schemas['CancelBody'];
export type DiscoveryCancelled = Schemas['CancelAccepted'] | Schemas['AlreadyFinished'];

export type DiscoveryResults = Schemas['Results'];
export type DiscoveryResultCard = Schemas['ResultCard'];
export type DiscoveryReelPreview = Schemas['ReelPreview'];
export type DiscoveryVettingRow = Schemas['VettingRow'];
export type DiscoveryVettingVerdict = Schemas['VettingVerdict'];
export type DiscoveryVerdictRow = Schemas['VerdictRow'];
export type DiscoveryScoreComponents = Schemas['ScoreComponents'];

export type DiscoveryPick = Schemas['CampaignPick'];
export type DiscoveryPicks = Schemas['CampaignPicks'];
export type DiscoveryPicksPatch = Schemas['PicksPatch'];
export type DiscoveryPersonaStarters = Schemas['PersonaStarters'];
export type DiscoveryPlatform = DiscoveryPicksPatch['platform'];
export type DiscoveryPlacement = NonNullable<DiscoveryPick['placed']>;

/**
 * Go's `POST /campaigns/{id}/discovery/media-links` contract (`internal/discovery/media_handler.go`); one item
 * failing `MediaItem.valid` in `media_token.go` rejects the whole batch.
 */
export type DiscoveryMediaItem =
  | { kind: 'image'; url: string; platform?: string; platformId?: string; postId?: string }
  | { kind: 'reel'; platform?: string; platformId: string; postId: string };

export interface DiscoveryMediaLinksBody {
  items: DiscoveryMediaItem[];
}

export interface DiscoveryMediaLinks {
  tokens: string[];
}
